import React, { useState, useContext } from "react";
import { Typography, Box, CardContent, Button, Container } from "@mui/material";
import {
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import Editor from "@monaco-editor/react";
import BaseOutputPage from "./BaseOutputPage";
import { LoadingModalContext } from "../../../../main-pages/ServicePage";
import MessagePopup from "../../../../popup-components/MessagePopup";
import { playwrightTypeScriptLocatorsAPI } from "../../../../../services/webServices";
import { CagLocatorsGeneration } from "../../../../../services/webServices";
import {
  getStepperSteps,
  getStepIndex,
  getNextStep,
} from "../../../../../utils/stepperConfig";
import CustomStepper from "../../../../custom-components/CustomStepper";

const validatePageSource = (pageSource) => {
  if (!pageSource || typeof pageSource !== "string") {
    return "Invalid input: Page source must be a string.";
  }

  const trimmedSource = pageSource.trim();
  if (!trimmedSource.startsWith("<") || !trimmedSource.endsWith(">")) {
    return "Invalid Page Source: Must start with '<' and end with '>'.";
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(trimmedSource, "text/html");
  const parserErrors = doc.querySelectorAll("parsererror");

  if (parserErrors.length > 0) {
    return "Invalid HTML Structure: Parsing failed. Please check your tags and structure.";
  }

  if (!doc.querySelector("html") || !doc.querySelector("body")) {
    return "Page source is missing <html> or <body> tags.";
  }

  return null; // Valid
};

const IntroduceSUTPage = () => {
  const [pageSourceInput, setPageSourceInput] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [generatedLocators, setGeneratedLocators] = useState(null);
  const { setLoadingState } = useContext(LoadingModalContext);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    automationType,
    preferences,
    environment,
    testCases,
    currentTestCaseIndex = 0,
    totalTestCases = 35,
    currentStep,
    currentStepIndex: stateStepIndex,
  } = location.state || {};

  // Helper function to get route path based on step name
  const getStepPath = (stepName) => {
    const stepPaths = {
      "Test Case Imported":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/test-case-imported",
      "Introduce SUT":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/introduce-sut",
      "Generate Locators":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-locators",
      "Generate Page Objects":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-page-objects",
      "Generate Test Files":
        "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-test-files",
    };
    return (
      stepPaths[stepName] ||
      "/dashboard/test-automation/playwright-typescript-mocha/ntnp/generate-locators"
    );
  };

  const handlePageSourceChange = (value) => {
    setPageSourceInput(value || "");
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleGenerateLocators = async () => {
    const validationError = validatePageSource(pageSourceInput);
    if (validationError) {
      setPopupMessage(validationError);
      setPopupOpen(true);
      return;
    }

    let fake;
    const startProgress = (setProgress) => {
      let progress = 0;
      let stop = false;
      let finalMessageShown = false;

      const interval = setInterval(() => {
        if (stop) return;

        progress += 2.4;
        if (progress >= 98 && !finalMessageShown) {
          finalMessageShown = true;
          setLoadingState((prev) => ({
            ...prev,
            message: "Almost done! Fetching your output",
          }));
        }

        if (progress >= 98) progress = 98;

        setProgress((prev) => ({
          ...prev,
          progress: Math.max(prev.progress, Math.floor(progress)),
        }));
      }, 200);

      return {
        stop: () => {
          stop = true;
          clearInterval(interval);
        },
        completeTo100: async () => {
          stop = true;
          clearInterval(interval);

          // Rush to 100%
          setProgress((prev) => ({
            ...prev,
            progress: 100,
          }));
          await new Promise((res) => setTimeout(res, 500));
        },
      };
    };

    const finishTo100AndClose = async () => {
      await fake.completeTo100();
      setLoadingState({ open: false, message: "", progress: null });
    };

    // Initial message
    setLoadingState({
      open: true,
      message: "Generating Locators",
      progress: 0,
    });

    fake = startProgress(setLoadingState);

    try {
      const requestData = {
        pageSource: pageSourceInput,
        testCases: testCases,
        automationType: automationType,
        preferences: preferences,
        environment: environment,
      };
      // const generatedLocatorsResponse =
      //   await playwrightTypeScriptLocatorsAPI(requestData);
      // const generatedLocators = generatedLocatorsResponse.data;

      const generatedLocatorsResponse =
        await CagLocatorsGeneration(requestData);
      const generatedLocators = generatedLocatorsResponse.data;

      // Debug logging
      console.log("🔍 Locators API response:", generatedLocatorsResponse);
      console.log("🔍 Generated locators data:", generatedLocators);
      console.log("🔍 Generated locators type:", typeof generatedLocators);
      console.log(
        "🔍 Generated locators is object:",
        typeof generatedLocators === "object"
      );
      console.log(
        "🔍 Generated locators keys:",
        generatedLocators ? Object.keys(generatedLocators) : "N/A"
      );
      console.log(
        "🔍 Generated locators keys length:",
        generatedLocators ? Object.keys(generatedLocators).length : 0
      );

      if (
        !generatedLocators ||
        typeof generatedLocators !== "object" ||
        Object.keys(generatedLocators).length === 0
      ) {
        console.log("❌ Locators validation failed");
        console.log("❌ Validation details:", {
          hasGeneratedLocators: !!generatedLocators,
          isObject: typeof generatedLocators === "object",
          keysLength: generatedLocators
            ? Object.keys(generatedLocators).length
            : 0,
        });
        setPopupMessage(
          "Locators generation failed. Please provide proper page source."
        );
        setPopupOpen(true);
        return;
      }
      await finishTo100AndClose();

      // Store generated locators and navigate to next step
      setGeneratedLocators(generatedLocators);

      // Navigate to Generate Locators page with the generated data
      const progressSteps = getStepperSteps(preferences);
      const currentStepIndex =
        stateStepIndex !== undefined
          ? stateStepIndex
          : getStepIndex(progressSteps, "Introduce SUT");
      const nextStep = getNextStep(progressSteps, currentStepIndex);

      if (nextStep) {
        const nextStepPath = getStepPath(nextStep);
        navigate(nextStepPath, {
          state: {
            automationType,
            preferences,
            environment,
            testCases,
            currentTestCaseIndex,
            totalTestCases,
            currentStep: nextStep,
            currentStepIndex: currentStepIndex + 1,
            generatedLocators, // Pass the generated locators to the next step
            pageSource: pageSourceInput, // Also pass the page source for reference
          },
        });
      }
    } catch (error) {
      console.error("Error generating locators:", error);
      const errorMsg =
        error?.response?.data?.Error ||
        error?.response?.data?.message ||
        "An error occurred. Please try again.";
      setPopupMessage(errorMsg);
      setPopupOpen(true);
      setLoadingState({ open: false, message: "", progress: null });
    } finally {
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  // Get dynamic steps based on framework selection
  const progressSteps = getStepperSteps(preferences);
  const currentStepIndex =
    stateStepIndex !== undefined
      ? stateStepIndex
      : getStepIndex(progressSteps, "Introduce SUT");

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#F6F3F2",
        display: "flex",
        flexDirection: "column",
        py: 4,
      }}
    >
      <Container maxWidth="lg" sx={{ width: "100%", flex: 1 }}>
        {/* Custom Stepper */}
        <Box sx={{ mb: 4 }}>
          <CustomStepper
            steps={progressSteps}
            activeStep={currentStepIndex}
            maxWidth="100%"
            containerWidth="100%"
            activeColor="#1976d2"
            completedColor="#40a9ff"
            pendingColor="#d1d1d1"
            showNextStep={true}
            showStepCount={true}
            title="Now"
            inProgressText="(In Progress)"
            completedText="(Completed)"
            nextText="Next"
            completedNextText="Completed"
            stepText="Step"
          />
        </Box>

        {/* Page Title */}
        <Typography
          variant="h4"
          component="h1"
          sx={{
            mb: { xs: 2, sm: 3, md: 4 },
            fontWeight: "bold",
            color: "#333",
            fontSize: { xs: "20px", sm: "24px", md: "28px" },
            fontFamily: "Manrope",
            lineHeight: { xs: 1.3, sm: 1.4 },
          }}
        >
          Provide Page DOM
        </Typography>

        {/* Main Content Card */}
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: { xs: "4px", sm: "8px" },
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            mb: { xs: 2, sm: 3, md: 4 },
            p: { xs: 2, sm: 3, md: 4 },
            overflow: "hidden",
          }}
        >
          {/* Page DOM Input */}
          <Box sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
            <Typography
              variant="body2"
              sx={{ 
                color: "#666", 
                fontFamily: "Manrope", 
                mb: { xs: 2, sm: 3 },
                fontSize: { xs: "13px", sm: "14px" },
                lineHeight: { xs: 1.4, sm: 1.5 },
              }}
            >
              Include only relevant DOM elements to avoid inaccurate locators
              and unnecessary automation complexity.
            </Typography>

            {/* Click to edit label */}
            <Typography
              variant="body2"
              sx={{
                color: "#666",
                mb: 2,
                fontFamily: "Manrope",
                fontSize: "14px",
                cursor: isEditing ? "default" : "pointer",
                "&:hover": {
                  color: isEditing ? "#666" : "#1976d2",
                },
              }}
              onClick={!isEditing ? handleEditClick : undefined}
            >
              {isEditing ? "Editing..." : "Click to edit"}
            </Typography>

            <Box
              sx={{
                width: "100%",
                bgcolor: "#000000",
                borderRadius: "8px",
                p: 0,
                border: "1px solid #d1d1d1",
                minHeight: "300px",
                position: "relative",
              }}
            >
              {/* Edit mode buttons */}
              {isEditing && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    zIndex: 1,
                    display: "flex",
                    gap: 1,
                  }}
                >
                  <Button
                    size="small"
                    onClick={handleSaveEdit}
                    sx={{
                      color: "#4caf50",
                      fontSize: "12px",
                      textTransform: "none",
                      minWidth: "auto",
                      p: 1,
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    onClick={handleCancelEdit}
                    sx={{
                      color: "#f44336",
                      fontSize: "12px",
                      textTransform: "none",
                      minWidth: "auto",
                      p: 1,
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              )}

              {/* Code Editor */}
              {isEditing ? (
                <Editor
                  height="300px"
                  defaultLanguage="html"
                  value={pageSourceInput}
                  onChange={handlePageSourceChange}
                  theme="vs-dark"
                  options={{
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    fontSize: 14,
                    fontFamily: "monospace",
                    lineNumbers: "on",
                    wordWrap: "on",
                    automaticLayout: true,
                    padding: { top: 16, bottom: 16 },
                    cursorStyle: "line",
                    selectOnLineNumbers: true,
                    roundedSelection: false,
                    readOnly: false,
                    contextmenu: true,
                    mouseWheelZoom: true,
                    smoothScrolling: true,
                    cursorBlinking: "blink",
                    cursorSmoothCaretAnimation: true,
                    placeholder: "Enter Page Source",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    padding: "16px",
                    color: "#ffffff",
                    fontFamily: "monospace",
                    fontSize: "14px",
                    lineHeight: 1.5,
                    whiteSpace: "pre-wrap",
                    overflow: "auto",
                    cursor: "pointer",
                    minHeight: "300px",
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                  onClick={handleEditClick}
                >
                  {pageSourceInput || "Enter Page Source"}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* Footer Navigation */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "transparent",
            borderRadius: "0 0 8px 8px",
          }}
        >
          {/* Back Button */}
          <Button
            variant="outlined"
            startIcon={<BackIcon />}
            onClick={() =>
              navigate(
                "/dashboard/test-automation/playwright-typescript-mocha/ntnp/test-case-imported",
                {
                  state: { testCases: testCases },
                }
              )
            }
            sx={{
              width: 120,
              height: 40,
              border: "2px solid #0094FB",
              borderRadius: 2,
              backgroundColor: "transparent",
              padding: "8px 16px",
              fontFamily: "Manrope",
              fontWeight: 700,
              fontSize: 20,
              lineHeight: "24px",
              letterSpacing: 0.15,
              color: "#0094FB",
              textTransform: "none",
              "&:hover": {
                border: "2px solid #0094FB",
                backgroundColor: "rgba(0, 148, 251, 0.04)",
              },
              transition: "all 0.3s ease-out",
            }}
          >
            Back
          </Button>

          {/* Next Button */}
          <Button
            variant="contained"
            endIcon={<NextIcon />}
            onClick={handleGenerateLocators}
            sx={{
              width: 240,
              height: 40,
              backgroundColor: "#0094FB",
              borderRadius: 2,
              padding: "8px 16px",
              fontFamily: "Manrope",
              fontWeight: 700,
              fontSize: 20,
              lineHeight: "24px",
              letterSpacing: 0.15,
              color: "white",
              textTransform: "none",
              border: "none",
              "&:hover": {
                backgroundColor: "#007ACC",
              },
              transition: "all 0.3s ease-out",
            }}
            disabled={!pageSourceInput.trim()}
          >
            Generate Locators
          </Button>
        </Box>
      </Container>

      <MessagePopup
        modalOpen={popupOpen}
        okayHandler={() => setPopupOpen(false)}
        message={popupMessage}
      />
    </Box>
  );
};

export default IntroduceSUTPage;
